import {
  ICopyDefaultToContractBody,
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreviewResponse,
} from "types/DefaultRules/DefaultRules";
import {
  IPackageRuleset,
  IPackageRulesetCreateRequest,
  IPackageRulesetUpdateRequest,
} from "types/PackageRuleset/PackageRuleset";
import {
  IAllPackageRulesetResponse,
  IPackageRulesetResponse,
} from "types/PackageRuleset/PackageRulesetResponse";

import { api } from ".";

const packageRulesetQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllPackageRulesets: builder.query<
      IAllPackageRulesetResponse,
      { isDefault?: boolean } | null | void
    >({
      query: (params) => {
        const search =
          params && params.isDefault !== undefined
            ? `?isDefault=${params.isDefault}`
            : "";
        return `package-rulesets/all${search}`;
      },
      providesTags: ["PackageRulesets"],
    }),
    getPackageRulesetById: builder.query<
      IPackageRulesetResponse,
      string | undefined
    >({
      query: (id: string) => `package-rulesets/${id}`,
      providesTags: (_r, _e, id) => [
        { type: "PackageRulesets", id: id ?? "LIST" },
      ],
    }),
    createPackageRuleset: builder.mutation<
      IPackageRuleset,
      IPackageRulesetCreateRequest
    >({
      query: (newRuleset) => ({
        url: "package-rulesets",
        method: "POST",
        body: newRuleset,
      }),
      invalidatesTags: ["PackageRulesets"],
    }),
    updatePackageRuleset: builder.mutation<
      IPackageRuleset,
      IPackageRulesetUpdateRequest
    >({
      query: (updatedRuleset) => ({
        url: `package-rulesets/${updatedRuleset.id}`,
        method: "PUT",
        body: updatedRuleset,
      }),
      invalidatesTags: ["PackageRulesets", "DefaultRulePropagation"],
    }),
    deletePackageRuleset: builder.mutation<any, string>({
      query: (id) => ({
        url: `package-rulesets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PackageRulesets", "DefaultRulePropagation"],
    }),
    clonePackageRulesetAsDefault: builder.mutation<
      IPackageRulesetResponse,
      string
    >({
      query: (id) => ({
        url: `package-rulesets/${id}/clone-as-default`,
        method: "POST",
      }),
      invalidatesTags: ["PackageRulesets"],
    }),
    copyPackageRulesetToContract: builder.mutation<
      IPackageRulesetResponse,
      { id: string; body: ICopyDefaultToContractBody }
    >({
      query: ({ id, body }) => ({
        url: `package-rulesets/${id}/copy-to-contract`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["PackageRulesets", "ContractFeeSchedules"],
    }),
    getPackageRulesetPropagationPreview: builder.query<
      IDefaultRulePropagationPreviewResponse,
      string
    >({
      query: (id) => `package-rulesets/${id}/propagate/preview`,
      providesTags: (_r, _e, id) => [
        { type: "DefaultRulePropagation", id: `package:${id}` },
      ],
    }),
    applyPackageRulesetPropagation: builder.mutation<
      IDefaultRulePropagationApplyResponse,
      { id: string; requests: IDefaultRulePropagationApplyRequest[] }
    >({
      query: ({ id, requests }) => ({
        url: `package-rulesets/${id}/propagate`,
        method: "POST",
        body: { requests },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "PackageRulesets",
        { type: "DefaultRulePropagation", id: `package:${id}` },
      ],
    }),
  }),
});

export const {
  useGetAllPackageRulesetsQuery,
  useGetPackageRulesetByIdQuery,
  useCreatePackageRulesetMutation,
  useUpdatePackageRulesetMutation,
  useDeletePackageRulesetMutation,
  useClonePackageRulesetAsDefaultMutation,
  useCopyPackageRulesetToContractMutation,
  useGetPackageRulesetPropagationPreviewQuery,
  useLazyGetPackageRulesetPropagationPreviewQuery,
  useApplyPackageRulesetPropagationMutation,
} = packageRulesetQueries;
